/**
 * Constants that exist only inside `backend/templates/SAMPLE JOB CARD V6_1.xlsx`.
 *
 * The HTML job card preview has to reproduce numbers that Excel computes from
 * cells baked into the template, so those cells are mirrored here. The mirror is
 * guarded by `backend/src/services/__tests__/job-card-template-constants.test.ts`,
 * which reads the real workbook and fails if the two ever drift apart. If you
 * change the template, change these values in the same commit.
 */

/** Worker rows on the JOB CARD sheet (29-34): first and last row, and capacity. */
export const JOB_CARD_WORKER_FIRST_ROW = 29;
export const JOB_CARD_WORKER_LAST_ROW = 34;
export const JOB_CARD_WORKER_CAPACITY =
  JOB_CARD_WORKER_LAST_ROW - JOB_CARD_WORKER_FIRST_ROW + 1; // 6

/**
 * Column D of rows 29-34 as they sit in the template.
 *
 * These are only reached when a job has NO worker hours: `populate_worker_hours`
 * returns early without writing anything, so the workbook keeps its own names
 * and these rates, and the preview must match. Once any worker is placed, each
 * one is priced at their own `workers.rate` (NEW-202) and these values are
 * overwritten — they are no longer per-ROW pricing for real jobs.
 */
export const JOB_CARD_WORKER_ROW_RATES = [34.5, 34.5, 31.5, 245, 31.5, 34.5] as const;

/**
 * Column A of rows 29-34 as they sit in the template. When a job has no worker
 * hours, `populate_worker_hours` returns early without writing anything, so the
 * workbook keeps these names — and so must the preview.
 */
export const JOB_CARD_WORKER_ROW_NAMES = [
  'Ciro', 'Margarito', 'Rodelfino', 'Ciro', 'Jose', 'Juan',
] as const;

/** Cell K44 — work comp / insurance / other fees, applied to the worker block (G36). */
export const JOB_CARD_WORK_COMP_RATE = 0.24;

/** Misc/Other Expenses rows 54-70 on page 2; more than this overflows to its own sheet. */
export const JOB_CARD_EXPENSE_CAPACITY = 17;

/** Row labels, so the preview reads like the sheet it mirrors. */
export const JOB_CARD_LABOR_LABELS = {
  management: 'MANAGEMENT',
  office: 'OFFICE',
  assistant: 'ASSISTANT/HELPER',
} as const;

export const JOB_CARD_REIMBURSEMENT_LABELS = {
  toll: 'TOLL',
  mileage: 'Mileage',
} as const;

/**
 * Rows 20-23, in sheet order. The second GAS row (old row 23) was removed from
 * the template, which shifted Home Depot up from row 24 to 23.
 */
export const JOB_CARD_DIRECT_COST_LABELS = {
  parking: 'PARKING (estimated)',
  gasEstimate: 'GAS (estimated)',
  materials: 'Materials (estimated)',
  homeDepot: 'Home Depot (estimated)',
} as const;

/** Column A of rows 20-23 — the direct-cost keys in the order they appear. */
export const JOB_CARD_DIRECT_COST_FIRST_ROW = 20;
